// Copyright (c) Ping. All rights reserved.
//
// CameraCaptureOverlay — web-first webcam capture modal. Opens the
// device camera via `navigator.mediaDevices.getUserMedia({ video })`,
// renders the live preview into a real DOM `<video>` element (the
// repo's web-only DOM pattern — see `ComposerGifOverlay`'s `<img>`
// via `const ImgTag: any = 'img'`), and on capture draws the current
// frame to an offscreen `<canvas>`, exports a JPEG `File`, hands it to
// `onCapture`, and closes.
//
// Leaf component: the host owns *when* to open it (`open`) and what to
// do with the captured `File` (stage it into the attachment tray).
//
// Platform: `getUserMedia` is web-only. On native (or any environment
// without `navigator.mediaDevices`) we render a graceful "Camera not
// available" card. Everything camera-touching is guarded on
// `typeof navigator !== 'undefined' && navigator.mediaDevices`.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Camera, X } from 'lucide-react-native';
import { tokens } from '../../design-system/tokens';

export interface CameraCaptureOverlayProps {
  /** Host-controlled visibility. When false the component renders null. */
  readonly open: boolean;
  /** Called with the captured JPEG frame as a `File`. */
  readonly onCapture: (file: File) => void;
  /** Called when the user cancels or capture completes. */
  readonly onClose: () => void;
  readonly testID?: string | undefined;
}

// react-native-web exposes the DOM globals; on native they're absent.
// `getUserMedia` lives behind `navigator.mediaDevices` and only exists
// on the web build.
function cameraSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

export const CameraCaptureOverlay = React.memo<CameraCaptureOverlayProps>(
  ({ open, onCapture, onClose, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    // The live `<video>` DOM node (web only). Typed loosely because RN
    // types don't model DOM elements; cast at the boundary.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const videoRef = useRef<any>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [ready, setReady] = useState(false);

    const supported = cameraSupported();

    // Tear down the active stream — stop every track so the camera
    // indicator turns off. Safe to call repeatedly.
    const stopStream = useCallback(() => {
      const stream = streamRef.current;
      if (stream !== null) {
        for (const track of stream.getTracks()) track.stop();
        streamRef.current = null;
      }
      const video = videoRef.current;
      if (video !== null && typeof video === 'object') {
        try {
          video.srcObject = null;
        } catch {
          // ignore — node may already be detached
        }
      }
      setReady(false);
    }, []);

    // Acquire the camera when the overlay opens; release it when it
    // closes or unmounts. Re-runs only on `open` / support changes.
    useEffect(() => {
      if (!open) return;
      if (!supported) {
        setError('Camera not available on this device.');
        return;
      }

      let cancelled = false;
      setError(null);
      setReady(false);

      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          if (cancelled) {
            for (const track of stream.getTracks()) track.stop();
            return;
          }
          streamRef.current = stream;
          const video = videoRef.current;
          if (video !== null && typeof video === 'object') {
            video.srcObject = stream;
            // Autoplay the preview; ignore the rejected promise that
            // some browsers return when play() races teardown.
            const playResult: unknown = video.play?.();
            if (
              playResult !== undefined &&
              playResult !== null &&
              typeof (playResult as Promise<void>).then === 'function'
            ) {
              (playResult as Promise<void>).catch(() => undefined);
            }
            setReady(true);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setError('Could not access the camera. Check permissions.');
          }
        });

      return () => {
        cancelled = true;
        stopStream();
      };
    }, [open, supported, stopStream]);

    // Belt-and-braces: always release the stream on unmount even if
    // the open-effect cleanup didn't fire (e.g. fast unmount).
    useEffect(() => stopStream, [stopStream]);

    const handleCancel = useCallback(() => {
      stopStream();
      onClose();
    }, [stopStream, onClose]);

    const handleCapture = useCallback(() => {
      const video = videoRef.current;
      if (
        typeof document === 'undefined' ||
        video === null ||
        typeof video !== 'object'
      ) {
        return;
      }

      const width: number = Number(video.videoWidth) || 0;
      const height: number = Number(video.videoHeight) || 0;
      if (width === 0 || height === 0) return;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx === null) return;
      ctx.drawImage(video, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob === null) return;
          const file = new File([blob], `camera-${Date.now()}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          stopStream();
          onCapture(file);
          onClose();
        },
        'image/jpeg',
        0.92,
      );
    }, [stopStream, onCapture, onClose]);

    if (!open) return null;

    const cardStyle: ViewStyle = {
      backgroundColor: colorScheme === 'dark' ? '#11131A' : colors.surface.primary,
      borderColor: colorScheme === 'dark' ? '#2F333B' : colors.border.secondary,
      borderWidth: StyleSheet.hairlineWidth,
    };

    return (
      // Dark scrim covering the host panel; the card is centered.
      <View
        className="absolute inset-0 z-50 items-center justify-center p-lg"
        style={{ backgroundColor: colors.overlay.scrim }}
        testID={testID}
      >
        <View
          className="w-[420px] max-w-full rounded-lg overflow-hidden p-lg gap-lg"
          style={cardStyle}
        >
          {/* Header: title + close affordance. */}
          <View className="flex flex-row items-center justify-between">
            <Text
              className="font-sans text-[16px] leading-5 font-semibold"
              style={{ color: colors.text.primary }}
            >
              Take a photo
            </Text>
            <Pressable
              onPress={handleCancel}
              accessibilityRole="button"
              accessibilityLabel="Cancel camera"
              className="w-8 h-8 items-center justify-center rounded-full"
            >
              <X size={20} strokeWidth={2} color={colors.text.secondary} />
            </Pressable>
          </View>

          {/* Live preview region — fixed 16:9 stage with rounded chrome. */}
          <View
            className="w-full rounded-md overflow-hidden items-center justify-center"
            style={{
              aspectRatio: 16 / 9,
              backgroundColor: colorScheme === 'dark' ? '#000000' : '#0B0B0D',
            }}
          >
            {supported && error === null ? (
              <CameraPreview videoRef={videoRef} />
            ) : (
              <View className="items-center justify-center gap-sm px-lg">
                <Camera size={28} strokeWidth={2} color={colors.text.tertiary} />
                <Text
                  className="font-sans text-[14px] leading-5 text-center"
                  style={{ color: colors.text.tertiary }}
                >
                  {error ?? 'Camera not available'}
                </Text>
              </View>
            )}
          </View>

          {/* Footer controls: Cancel + circular shutter. */}
          <View className="flex flex-row items-center justify-between">
            <Pressable
              onPress={handleCancel}
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              className="h-10 px-lg items-center justify-center rounded-md"
            >
              <Text
                className="font-sans text-[14px] leading-5 font-medium"
                style={{ color: colors.text.secondary }}
              >
                Cancel
              </Text>
            </Pressable>

            {/* Circular capture (shutter) button — inner ring on top of
                a solid disc, design-system "camera shutter". */}
            <Pressable
              onPress={handleCapture}
              disabled={!supported || error !== null || !ready}
              accessibilityRole="button"
              accessibilityLabel="Capture photo"
              testID={testID !== undefined ? `${testID}-capture` : undefined}
              className={`w-16 h-16 rounded-full items-center justify-center border-2 ${
                !supported || error !== null || !ready ? 'opacity-40' : ''
              }`}
              style={{ borderColor: colors.text.primary }}
            >
              <View
                className="w-12 h-12 rounded-full"
                style={{ backgroundColor: colors.text.primary }}
              />
            </Pressable>

            {/* Spacer to balance the Cancel button so the shutter stays
                visually centered. */}
            <View className="h-10 px-lg" />
          </View>
        </View>
      </View>
    );
  },
);

CameraCaptureOverlay.displayName = 'CameraCaptureOverlay';

// Web-only DOM `<video>` host. On native `document` is undefined so we
// render nothing (the parent already shows the "not available" card via
// the `supported` guard). Inline styles stay on the raw DOM node since
// plain DOM elements can't consume NativeWind classNames.
interface CameraPreviewProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly videoRef: React.MutableRefObject<any>;
}

const CameraPreview: React.FC<CameraPreviewProps> = ({ videoRef }) => {
  if (typeof document === 'undefined') return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const VideoTag: any = 'video';
  return (
    <VideoTag
      ref={videoRef}
      autoPlay
      playsInline
      muted
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        // Mirror the preview like a selfie cam — feels natural to users.
        transform: 'scaleX(-1)',
      }}
    />
  );
};
